import { Typography } from '@healingpaper/cell'
import { IcArrowRight } from '@healingpaper/cell-icons'
import { useState } from 'react'
import { createFetchWebEventCollector, type Currency, EventName, PageName, type Predecessor } from 'web-event-collector'
import { getEnv } from '@unni/platform'

import { postAmplitude } from '@/core/lib'
import { ServiceOfferCard } from '@/features/service-offer/components/service-offer-card'
import { useTranslation } from '@/lang'
import { createCommonEvent } from '@/utils/eventCollector'
import { generateTagId } from '@/utils/test-automation'
import theme from '@/theme'

import type { ServiceOffer } from '@/features/service-offer/service-offer.type'
import type { HospitalView } from '@/features/hospital/types/hospital.type'
import type { User } from '@/features/member/member.type'

const MIN_EVENT_LENGTH = 4
export const HospitalEventSection = ({
  className,
  events,
  hospital,
  user,
}: {
  className?: string
  events: ServiceOffer[]
  hospital: HospitalView
  user: User | null
}) => {
  const { t } = useTranslation()
  const [eventLength, setEventLength] = useState(MIN_EVENT_LENGTH)
  const { emitServiceOfferViewed, emitServiceOfferClicked } = createFetchWebEventCollector({
    baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
  })

  const storePredecessorAndEmitServiceOfferClick = ({
    event,
    index,
    userId,
    hospitalId,
  }: {
    event: ServiceOffer
    index: number
    userId: number
    hospitalId: number
  }) => {
    const commonEvent = createCommonEvent(navigator.userAgent, userId)

    const predecessor: Predecessor = {
      Name: EventName.ServiceOfferClicked,
      TraceId: commonEvent.TraceId,
    }

    localStorage.setItem('predecessor', JSON.stringify(predecessor))

    emitServiceOfferClicked({
      ...commonEvent,
      Name: EventName.ServiceOfferClicked,
      Body: {
        ...commonEvent.Body,
        hospitalId,
        serviceOfferId: event.id,
        serviceOfferType: 'SPECIAL_OFFER',
        placementOrderInPage: 4,
        itemOrderInPlacement: index + 1,
        sellingPriceAmount: event.price.originalAmount,
        sellingPriceCurrency: event.price.currency as Currency,
        pageName: PageName.HospitalDetailWeb,
      },
    })
  }

  const trackClickEvent = (event: ServiceOffer, index: number) => {
    if (user?.id) {
      storePredecessorAndEmitServiceOfferClick({
        event,
        index,
        userId: user?.id || 0,
        hospitalId: hospital.hospitalId,
      })
    }
    postAmplitude('click_event_in_hospital_detail', {
      event_id: event.id,
      event_title: event.title,
      op_tag_list: event.badges,
    })
  }

  const emitServiceOfferViewedEvent = (event: ServiceOffer, index: number, user: User | null) => {
    if (user === null || user.id === 0 || event === null) {
      return null
    }

    const commonEvent = createCommonEvent(navigator.userAgent, user.id)
    emitServiceOfferViewed({
      ...commonEvent,
      Name: EventName.ServiceOfferViewed,
      Body: {
        ...commonEvent.Body,
        hospitalId: event.hospital.id,
        serviceOfferId: event.id,
        serviceOfferType: 'SPECIAL_OFFER',
        placementOrderInPage: 4,
        itemOrderInPlacement: index + 1,
        sellingPriceAmount: event.price.originalAmount,
        sellingPriceCurrency: event.price.currency as Currency,
        pageName: PageName.HospitalDetailWeb,
      },
    })
  }

  return (
    <div data-testid="event-section" className={`border-t border-gray-200 ${className}`}>
      <div style={{ padding: '26px 26px 0' }}>
        <Typography type="title3" elementType="h2">
          {t('templates.hospitalDetail.eventTitle')}{' '}
          <span style={{ color: theme.colors.orange }}>{events.length.toLocaleString()}</span>
          {t('templates.hospitalDetail.countUnit')}
        </Typography>
      </div>

      <div className="flex flex-col gap-[1px] bg-[#ECECEC]">
        {events.slice(0, eventLength).map((event: ServiceOffer, index: number) => {
          const { id: serviceOfferId, title, thumbnail, rating, price } = event
          const { totalRating: ratingAmount, ratingCount } = rating || {}
          const { originalAmount, discountedAmount, currency, exchangeRateAppliedPrice } = price || {}
          const {
            discountedAmount: exchangeRateDiscountedAmount,
            originalAmount: exchangeRateOriginalAmount,
            currency: exchangeRateCurrency,
          } = exchangeRateAppliedPrice || {}

          const sido = hospital.location.sido
          const subway = hospital.location?.subwayNames?.join(' ')
          const name = hospital.name

          const finalOriginalAmount = exchangeRateOriginalAmount || originalAmount
          const finalDiscountedAmount = exchangeRateDiscountedAmount || discountedAmount
          const finalCurrency = exchangeRateCurrency || currency

          return (
            <ServiceOfferCard
              elementId={generateTagId('event', 'card', 'component', 'ui', index.toString())}
              key={`${event.id}_${index} `}
              eventId={serviceOfferId}
              thumbnailUrl={thumbnail}
              title={title}
              subtitle={`${sido}${subway ? `・${subway}` : ''}${name ? `・${name}` : ''} `}
              review={{
                rating: ratingAmount,
                count: ratingCount,
              }}
              price={{
                originalCost: finalOriginalAmount,
                discountedCost: finalDiscountedAmount,
                currency: finalCurrency,
              }}
              titleElementType="h2"
              priceElementType="h3"
              trackClickEvent={() => trackClickEvent(event, index)}
              onImpressed={() => emitServiceOfferViewedEvent(event, index, user)}
            />
          )
        })}
      </div>
      <div className="flex flex-col pt-2 pb-6 px-5">
        {events.length > MIN_EVENT_LENGTH && events.length !== eventLength && (
          <button
            className="flex flex-row items-center justify-center px-4 py-2 w-full h-[36px] bg-[#ECECEC] rounded-[8px]"
            onClick={() => {
              setEventLength(events.length)
              postAmplitude('click_more_event_in_hospital_detail', {
                count_item: events.length,
              })
            }}
          >
            <Typography type="title4" elementType="span">
              {t('templates.hospitalDetail.buttons.moreEvent', { count: events.length })}
            </Typography>
            <IcArrowRight width={16} height={16} />
          </button>
        )}
      </div>
    </div>
  )
}
